import React, { useState } from 'react';
import { Card, Form, Input, Button, Typography, message, Avatar, Space, Divider, Tabs } from 'antd';
import { UserOutlined, MailOutlined, PhoneOutlined, SaveOutlined, LockOutlined } from '@ant-design/icons';
import { useAuthStore } from '@/store/authStore';

const { Title } = Typography;

const Profile: React.FC = () => {
  const { user, updateProfile, changePassword } = useAuthStore();
  const [profileLoading, setProfileLoading] = useState(false);
  const [passwordLoading, setPasswordLoading] = useState(false);
  const [profileForm] = Form.useForm();
  const [passwordForm] = Form.useForm();

  const onProfileFinish = async (values: any) => {
    setProfileLoading(true);
    try {
      await updateProfile(values);
      message.success('Perfil atualizado com sucesso!');
    } catch (error: any) {
      message.error(error.response?.data?.detail || 'Erro ao atualizar perfil.');
    } finally {
      setProfileLoading(false);
    }
  };

  const onPasswordFinish = async (values: any) => {
    setPasswordLoading(true);
    try {
      await changePassword(values.old_password, values.new_password);
      message.success('Senha alterada com sucesso!');
      passwordForm.resetFields();
    } catch (error: any) {
      message.error(error.response?.data?.error || 'Erro ao alterar senha.');
    } finally {
      setPasswordLoading(false);
    }
  };

  const tabItems = [
    {
      key: 'profile',
      label: 'Dados Pessoais',
      children: (
        <Form
          form={profileForm}
          layout="vertical"
          onFinish={onProfileFinish}
          initialValues={{
            first_name: user?.first_name,
            last_name: user?.last_name,
            email: user?.email,
            username: user?.username,
            phone: user?.phone,
          }}
        >
          <Form.Item name="first_name" label="Nome" rules={[{ required: true }]}>
            <Input prefix={<UserOutlined />} />
          </Form.Item>

          <Form.Item name="last_name" label="Sobrenome" rules={[{ required: true }]}>
            <Input prefix={<UserOutlined />} />
          </Form.Item>

          <Form.Item name="email" label="E-mail" rules={[{ required: true }, { type: 'email' }]}>
            <Input prefix={<MailOutlined />} />
          </Form.Item>

          <Form.Item name="username" label="Nome de Usuário" rules={[{ required: true }]}>
            <Input />
          </Form.Item>

          <Form.Item name="phone" label="Telefone">
            <Input prefix={<PhoneOutlined />} />
          </Form.Item>

          <Form.Item>
            <Button type="primary" htmlType="submit" loading={profileLoading} icon={<SaveOutlined />}>
              Salvar Alterações
            </Button>
          </Form.Item>
        </Form>
      ),
    },
    {
      key: 'password',
      label: 'Alterar Senha',
      children: (
        <Form
          form={passwordForm}
          layout="vertical"
          onFinish={onPasswordFinish}
        >
          <Form.Item
            name="old_password"
            label="Senha Atual"
            rules={[{ required: true, message: 'Informe a senha atual' }]}
          >
            <Input.Password prefix={<LockOutlined />} />
          </Form.Item>

          <Form.Item
            name="new_password"
            label="Nova Senha"
            rules={[
              { required: true, message: 'Informe a nova senha' },
              { min: 8, message: 'Mínimo de 8 caracteres' },
            ]}
          >
            <Input.Password prefix={<LockOutlined />} />
          </Form.Item>

          <Form.Item
            name="new_password_confirm"
            label="Confirmar Nova Senha"
            dependencies={['new_password']}
            rules={[
              { required: true, message: 'Confirme a nova senha' },
              ({ getFieldValue }) => ({
                validator(_, value) {
                  if (!value || getFieldValue('new_password') === value) {
                    return Promise.resolve();
                  }
                  return Promise.reject(new Error('As senhas não conferem'));
                },
              }),
            ]}
          >
            <Input.Password prefix={<LockOutlined />} />
          </Form.Item>

          <Form.Item>
            <Button type="primary" htmlType="submit" loading={passwordLoading} icon={<LockOutlined />}>
              Alterar Senha
            </Button>
          </Form.Item>
        </Form>
      ),
    },
  ];

  return (
    <div>
      <Title level={4}>Meu Perfil</Title>

      <Card>
        <div style={{ display: 'flex', alignItems: 'center', marginBottom: 24 }}>
          <Avatar size={80} icon={<UserOutlined />} src={user?.avatar} />
          <div style={{ marginLeft: 24 }}>
            <Title level={3} style={{ margin: 0 }}>{user?.full_name}</Title>
            <Space>
              <span style={{ color: '#666' }}>{user?.email}</span>
              <span style={{ color: '#999' }}>|</span>
              <span style={{ color: '#1890ff' }}>{user?.profile}</span>
            </Space>
          </div>
        </div>

        <Divider />

        <Tabs items={tabItems} />
      </Card>
    </div>
  );
};

export default Profile;
